import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { Control, FieldErrors, FieldNamesMarkedBoolean } from 'react-hook-form'
import BaseSelect from 'src/components/Inputs/BaseSelect'
import BaseTextField from 'src/components/Inputs/BaseTextField'
import PhoneField from 'src/components/Inputs/PhoneField'
import {
  OnDemandPersonaType,
  OnDemandPickupLogistics,
  OnDemandPickupTiming,
  OnDemandVolumeEstimate,
  StorageTime
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { DEFAULT_PER_SQM_RATES, OnDemandCityConfig } from 'src/utils/onDemand'

import { getTierDiscountPct } from './helpers'
import PriceReveal from './PriceReveal'
import { IWizardForm } from './types'

const NAME_MIN_LENGTH = 2

interface IStepCommonProps {
  control: Control<IWizardForm>
  errors: FieldErrors<IWizardForm>
}

interface IStepLocationProps extends IStepCommonProps {
  /** When set (city page), the pickup city is rendered as a locked label
   *  and the select is hidden. */
  coverage?: OnDemandCityConfig
  /** When set (parent page) with more than one active city, render the
   *  select with all active cities; otherwise show the single locked
   *  city. */
  cityOptions: OnDemandCityConfig[]
  watchedCity: string | undefined
  /** Zones for the currently-resolved pickup city — sourced from the
   *  city's `coverageZones`. Empty array hides the zone field entirely
   *  (e.g. cities without partner-provided zone coverage). */
  zoneOptions: string[]
}

/**
 * Step 1 — Location. Lowest-commitment ask, designed as the foot-in-the-door
 * before asking for anything else. One required field (`pickupCity` on
 * parent / `pickupAddress` always), and `pickupCity` is auto-resolved or
 * pre-locked on city pages so the user only types their colonia/dirección
 * to advance.
 */
export const StepLocation = ({
  control,
  coverage,
  cityOptions,
  watchedCity,
  zoneOptions
}: IStepLocationProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const cityIsLocked = !!coverage
  const showCitySelect = !cityIsLocked && cityOptions.length > 1
  const cityEntries: Record<string, string> = cityOptions.reduce(
    (acc, city) => {
      acc[city.canonicalSlug] = city.displayName
      return acc
    },
    {} as Record<string, string>
  )

  // Zone select replaces the legacy free-text `pickupAddress` field. PostHog
  // funnel data (insight 2026-05-19, 99 storage_on_demand_view → 12 step 2
  // = 86% drop on step 1) attributed the abandonment to the unstructured
  // address field — users either typed nothing and bounced, or didn't know
  // what to type. A short bounded select sourced from the partner's
  // `coverageZones` collapses the decision to a one-tap choice. Optional,
  // so users who don't see their zone can still advance.
  const zoneEntries: Record<string, string> = zoneOptions.reduce(
    (acc, zone) => {
      acc[zone] = zone
      return acc
    },
    {} as Record<string, string>
  )
  // Step 1 asks exactly ONE thing. When a city SELECT is shown (parent page,
  // 2+ active cities) that select is the single ask, so the optional zone is
  // suppressed. When the city is pre-locked or single (city pages, the
  // primary on-demand surface), the zone select becomes step 1's single ask
  // and the lead_start focus target. Never both at once — a two-field step 1
  // is the friction the `storage_on_demand_lead_start` drop is fighting.
  // (Parent-page zone is dropped entirely; sales confirms the precise address
  // on WhatsApp — it was already optional and non-validating.)
  const showZoneSelect = zoneOptions.length > 0 && !showCitySelect

  return (
    <div className="flex flex-col gap-4">
      {showCitySelect ? (
        <BaseSelect
          id="pickupCity"
          control={control}
          topLabel={t('FORM.FIELDS.PICKUP_CITY.LABEL')}
          entries={cityEntries}
          rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
          required
          fullWidth
        />
      ) : (
        <div className="rounded-md border border-ink-100/15 bg-cream-300/40 px-3 py-2">
          <p className="text-[11px] tracking-[0.06em] text-ink-200 uppercase">
            {t('FORM.FIELDS.PICKUP_CITY.LABEL')}
          </p>
          <p className="mt-0.5 font-display text-[15px] font-medium text-ink-700">
            {coverage?.displayName ?? cityOptions[0]?.displayName ?? ''}
          </p>
          <input
            type="hidden"
            value={watchedCity ?? ''}
            name="pickupCity"
            readOnly
          />
        </div>
      )}

      {/* Zone select — bounded list from the partner's `coverageZones`.
          Optional. Hidden when no zones are configured for the resolved
          city (e.g. cities pre-wired with `partnerActive: false` and empty
          `coverageZones`). The legacy free-text `pickupAddress` field was
          removed because PostHog showed 86% abandonment on this step;
          sales picks up the precise address verbally on WhatsApp. */}
      {showZoneSelect && (
        <div>
          <BaseSelect
            id="pickupZone"
            control={control}
            topLabel={t('FORM.FIELDS.PICKUP_ZONE.LABEL')}
            entries={zoneEntries}
            displayEmpty
            fullWidth
          />
          <p className="mt-1 text-[12px] leading-snug text-ink-200">
            {t('FORM.FIELDS.PICKUP_ZONE.HELPER')}
          </p>
        </div>
      )}
    </div>
  )
}

interface IStepVolumeAndDurationProps extends IStepCommonProps {
  watchedVolume: OnDemandVolumeEstimate | undefined
  watchedDuration: string | undefined
  perSqmRates: OnDemandCityConfig['perSqmRates'] | undefined
}

/**
 * Step 2 — Volume + Duration. The "reward" step: as the user picks each
 * field, the `PriceReveal` chip above produces a live estimate, anchoring
 * the conversion on a concrete number. This is the strongest sunk-cost
 * lever — after this step they've "earned" a personalized price.
 */
export const StepVolumeAndDuration = ({
  control,
  watchedVolume,
  watchedDuration,
  perSqmRates
}: IStepVolumeAndDurationProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const VOLUME_ENTRIES: Record<OnDemandVolumeEstimate, string> = {
    [OnDemandVolumeEstimate.Small]: t('FORM.FIELDS.VOLUME.OPTIONS.SMALL'),
    [OnDemandVolumeEstimate.Medium]: t('FORM.FIELDS.VOLUME.OPTIONS.MEDIUM'),
    [OnDemandVolumeEstimate.Large]: t('FORM.FIELDS.VOLUME.OPTIONS.LARGE'),
    [OnDemandVolumeEstimate.Unsure]: t('FORM.FIELDS.VOLUME.OPTIONS.UNSURE')
  }

  // "−N%" on the 6+/12+ options is computed from THIS market's rates.
  const durationRates = perSqmRates ?? DEFAULT_PER_SQM_RATES
  const DURATION_ENTRIES: Partial<Record<StorageTime, string>> = {
    [StorageTime.Month]: t('FORM.FIELDS.STORAGE_DURATION.OPTIONS.MONTH'),
    [StorageTime.FewMonths]: t(
      'FORM.FIELDS.STORAGE_DURATION.OPTIONS.FEW_MONTHS'
    ),
    [StorageTime.FewMonthsSix]: t(
      'FORM.FIELDS.STORAGE_DURATION.OPTIONS.FEW_MONTHS_SIX',
      { pct: getTierDiscountPct(durationRates, 'sixMonths') }
    ),
    [StorageTime.FewMonthsTwelve]: t(
      'FORM.FIELDS.STORAGE_DURATION.OPTIONS.FEW_MONTHS_TWELVE',
      { pct: getTierDiscountPct(durationRates, 'twelveMonths') }
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <PriceReveal
        volume={watchedVolume}
        storageDuration={(watchedDuration as StorageTime) || null}
        perSqmRates={perSqmRates}
      />

      <BaseSelect
        id="volumeEstimate"
        control={control}
        topLabel={t('FORM.FIELDS.VOLUME.LABEL')}
        entries={VOLUME_ENTRIES}
        rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
        required
        fullWidth
      />

      <BaseSelect
        id="storageDuration"
        control={control}
        topLabel={t('FORM.FIELDS.STORAGE_DURATION.LABEL')}
        entries={DURATION_ENTRIES as Record<string, string>}
        rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
        required
        fullWidth
      />
    </div>
  )
}

interface IStepLogisticsProps extends IStepCommonProps {}

/**
 * Step 3 — Pickup logistics. Three short selects (timing, persona,
 * logistics) that let the partner triage the lead. Volume and duration
 * already collected in step 2 — they're the price-bearing fields, and
 * logistics is metadata for the operations team rather than the customer.
 */
export const StepLogistics = ({ control }: IStepLogisticsProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const PERSONA_ENTRIES: Record<OnDemandPersonaType, string> = {
    [OnDemandPersonaType.PersonaFisica]: t(
      'FORM.FIELDS.PERSONA_TYPE.OPTIONS.PERSONA_FISICA'
    ),
    [OnDemandPersonaType.PersonaMoral]: t(
      'FORM.FIELDS.PERSONA_TYPE.OPTIONS.PERSONA_MORAL'
    )
  }
  const TIMING_ENTRIES: Record<OnDemandPickupTiming, string> = {
    [OnDemandPickupTiming.UrgentTodayTomorrow]: t(
      'FORM.FIELDS.PICKUP_TIMING.OPTIONS.URGENT_TODAY_TOMORROW'
    ),
    [OnDemandPickupTiming.ThisWeek]: t(
      'FORM.FIELDS.PICKUP_TIMING.OPTIONS.THIS_WEEK'
    ),
    [OnDemandPickupTiming.NextTwoWeeks]: t(
      'FORM.FIELDS.PICKUP_TIMING.OPTIONS.NEXT_TWO_WEEKS'
    ),
    [OnDemandPickupTiming.Flexible_30Plus]: t(
      'FORM.FIELDS.PICKUP_TIMING.OPTIONS.FLEXIBLE_30_PLUS'
    )
  }
  const LOGISTICS_ENTRIES: Record<OnDemandPickupLogistics, string> = {
    [OnDemandPickupLogistics.GroundFloor]: t(
      'FORM.FIELDS.PICKUP_LOGISTICS.OPTIONS.GROUND_FLOOR'
    ),
    [OnDemandPickupLogistics.Elevator]: t(
      'FORM.FIELDS.PICKUP_LOGISTICS.OPTIONS.ELEVATOR'
    ),
    [OnDemandPickupLogistics.Stairs]: t(
      'FORM.FIELDS.PICKUP_LOGISTICS.OPTIONS.STAIRS'
    ),
    [OnDemandPickupLogistics.ComplicatedAccess]: t(
      'FORM.FIELDS.PICKUP_LOGISTICS.OPTIONS.COMPLICATED_ACCESS'
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <BaseSelect
        id="pickupTiming"
        control={control}
        topLabel={t('FORM.FIELDS.PICKUP_TIMING.LABEL')}
        entries={TIMING_ENTRIES}
        rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
        required
        fullWidth
      />

      <div>
        <BaseSelect
          id="pickupLogistics"
          control={control}
          topLabel={t('FORM.FIELDS.PICKUP_LOGISTICS.LABEL')}
          entries={LOGISTICS_ENTRIES}
          rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
          required
          fullWidth
        />
        <p className="mt-1 text-[12px] leading-snug text-ink-200">
          {t('FORM.FIELDS.PICKUP_LOGISTICS.HELPER')}
        </p>
      </div>

      <BaseSelect
        id="personaType"
        control={control}
        topLabel={t('FORM.FIELDS.PERSONA_TYPE.LABEL')}
        entries={PERSONA_ENTRIES}
        rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
        required
        fullWidth
      />
    </div>
  )
}

interface IStepContactProps extends IStepCommonProps {
  /**
   * Per-field "has the user blurred this at least once" map from RHF.
   * Gates the red-border + inline-error UI so the fields never flash red
   * on first arrival even if `errors.X` has been populated by a prior
   * `trigger`, silent submit (WhatsApp escape hatch), or back-then-
   * forward navigation. `mode: 'onTouched'` on the form is necessary
   * but not sufficient — `errors` accumulates and we render off it, so
   * we additionally require `touchedFields[X]` before showing red.
   */
  touchedFields: Partial<Readonly<FieldNamesMarkedBoolean<IWizardForm>>>
}

/**
 * Step 4 — Contact ("the ask"). Name + WhatsApp + optional email. By this
 * point the user has invested 3 steps of effort and seen a personalized
 * price; the cost of bailing on the contact ask is higher than just typing
 * their phone number. Honeypot field lives here too — it's the natural
 * place for any "extra" input a bot would auto-fill.
 *
 * Notes field intentionally NOT rendered here: it bloated the step vertical
 * height enough to push the hero off-screen on shorter viewports, and sales
 * almost never reads freeform notes on these leads (the structured fields
 * — city, volume, duration, logistics, persona — cover what they need).
 * The backend's `submitOnDemandLead` mutation still accepts `notes` if a
 * future surface wants to capture it.
 */
export const StepContact = ({
  control,
  errors,
  touchedFields
}: IStepContactProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  // Only surface the error UI once the user has actually blurred the
  // field. Prevents red flash on arrival when `errors.X` is populated
  // by an earlier `trigger`, silent submit, or step back-and-forward.
  const showNameError = !!(touchedFields.name && errors.name)
  const showEmailError = !!(touchedFields.email && errors.email)

  return (
    <div className="flex flex-col gap-4">
      {/* Honeypot — visually hidden, real users never see it. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        <label htmlFor="company_url">Company URL</label>
        <BaseTextField
          id="company_url"
          control={control}
          tabIndex={-1}
          autoComplete="off"
          placeholder="https://example.com"
          hideCounter
        />
      </div>

      <div>
        <BaseTextField
          id="name"
          control={control}
          topLabel={t('FORM.FIELDS.NAME.LABEL')}
          placeholder={t('FORM.FIELDS.NAME.PLACEHOLDER')}
          required
          fullWidth
          rules={{
            required: t('FORM.VALIDATION.REQUIRED'),
            minLength: {
              value: NAME_MIN_LENGTH,
              message: t('FORM.VALIDATION.NAME_TOO_SHORT')
            }
          }}
          error={showNameError}
          slotProps={{ htmlInput: { 'aria-required': 'true' } }}
          className="w-full"
        />
        {showNameError && errors.name?.message ? (
          <p role="alert" className="mt-1 text-xs text-red-500">
            {errors.name.message}
          </p>
        ) : null}
      </div>

      <PhoneField<IWizardForm>
        name="phone"
        control={control}
        rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
        topLabel={t('FORM.FIELDS.PHONE.LABEL')}
        placeholder={t('FORM.FIELDS.PHONE.PLACEHOLDER')}
        required
        fullWidth
        hideErrorUntilTouched
      />

      {/* Optional email — drives the post-submit confirmation email and a
          sales fallback channel when WhatsApp doesn't connect. Validation
          is shape-only (regex), never required, never blocking. A typo
          must NOT prevent submit because phone is the primary contact. */}
      <div>
        <BaseTextField
          id="email"
          control={control}
          type="email"
          topLabel={t('FORM.FIELDS.EMAIL.LABEL')}
          placeholder={t('FORM.FIELDS.EMAIL.PLACEHOLDER')}
          fullWidth
          rules={{
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t('FORM.VALIDATION.EMAIL_INVALID')
            }
          }}
          error={showEmailError}
          autoComplete="email"
          className="w-full"
        />
        <p className="mt-1 text-[12px] leading-snug text-ink-200">
          {t('FORM.FIELDS.EMAIL.HELPER')}
        </p>
        {showEmailError && errors.email?.message ? (
          <p role="alert" className="mt-1 text-xs text-red-500">
            {errors.email.message}
          </p>
        ) : null}
      </div>

      {/* Privacy notice — the only point in this wizard where the renter
          is told their details leave SpotMe.

          🔴 This funnel discloses MORE than the listing LeadForm does:
          an on-demand lead is dispatched to the market's partner teams
          (`notifyOnDemandPartnersForExistingLead` /
          `dispatchHostLead`) at the FULL tier, which carries name +
          phone + email + notes AND `pickupAddress` — the renter's home.
          There is no listing on these leads, so the SpotMe-Now hard veto
          in `resolveHostLeadDisclosure` cannot fire and every one of
          them is full-disclosure by construction. Aviso de Privacidad §V
          permits the transfer; this line is what makes it disclosed.
          Never remove it, and never soften it into a "no compartimos"
          promise (see root CLAUDE.md → "Code ↔ Terms ↔ Marketing ↔ Help
          Center"). Opens in a new tab so the wizard keeps its state. */}
      <p className="text-[11.5px] leading-snug text-ink-200">
        {`${t('FORM.PRIVACY')} `}
        <Link
          href={RedirectUrls.Disclosures}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 transition-colors hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
        >
          {t('FORM.PRIVACY_LINK')}
        </Link>
      </p>
    </div>
  )
}
